import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Typography,
} from '@mui/material';
import { useForm } from 'react-hook-form';
import { AccordionListItem } from 'components/Button';
import { useSnackbar } from 'notistack';
import { useAssignUser } from '../../api/assignUser';
import { searchCompanyUsers } from 'features/companies/api/getCompanyUsers';
import { CardAutocomplete, FormProvider } from 'components/Form';

const UserItem = ({ full_name, ...rest }) => (
  <Stack gap={1} alignItems={'flex-start !important'} {...rest}>
    <Typography variant="body1" fontWeight="bold">
      {full_name}
    </Typography>
  </Stack>
);

export const AssignUserForm = ({ jobAd, refetch }) => {
  const [open, setOpen] = useState(false);

  // Intermediator ads are owned by the supplier, all others by the buyer.
  const ownerCompanyID = jobAd?.owner_type === 'Intermediator' ? jobAd?.supplier_id : jobAd?.buyer_id;
  const { enqueueSnackbar } = useSnackbar();
  const methods = useForm();

  const assignUserMutation = useAssignUser({
    onSuccess: () => {
      enqueueSnackbar('Assigned user updated successfully!', { variant: 'success' });
      setOpen(false);
      refetch();
    },
    onError: (error) => {
      enqueueSnackbar(error.response?.data?.error || 'Failed to update assigned user', { variant: 'error' });
    },
  });

  useEffect(() => {
    if (open) {
      methods.reset({
        assigned_user_id: jobAd?.assigned_user_id || null,
      });
    }
  }, [open, jobAd, methods]);


  const onSubmit = (data) => {
    assignUserMutation.mutate({
      adId: jobAd.ad_id,
      assignedUserId: data.assigned_user_id,
    });
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      <AccordionListItem onClick={handleOpen}>Assign User</AccordionListItem>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <FormProvider methods={methods} onSubmit={onSubmit}>
          <DialogTitle>Assign User to Job Ad: {jobAd?.headline}</DialogTitle>
          <DialogContent>
            <Box mt={2}>
              <CardAutocomplete
                name="assigned_user_id"
                label="Select Assigned User"
                queryFunction={(search) => searchCompanyUsers(ownerCompanyID, search)}
                optionLabelName="full_name"
                ItemCard={UserItem}
                onChange={(newValue) => {
                  methods.setValue('assigned_user_id', newValue ? newValue.user_id : null);
                }}
                value={jobAd.assigned_user_id ? { user_id: jobAd.assigned_user_id, full_name: jobAd.assigned_user_name, label: jobAd.assigned_user_name} : null}
                disabled={!ownerCompanyID}
                skipCreatingShadow={true}
                fetchOnOpen={true}
                fullWidth
                wrapperSx={{ mb: 2 }}
                loadingInitial
                changeSelectedValueOnChange={true}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={methods.handleSubmit(onSubmit)} disabled={assignUserMutation.isLoading}>
              {assignUserMutation.isLoading ? 'Assigning...' : 'Assign'}
            </Button>
          </DialogActions>
        </FormProvider>
      </Dialog>
    </>
  );
};